import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subject, timer } from 'rxjs';
import { mergeMap, take, map, takeWhile, finalize, filter, catchError, switchMap, takeUntil, tap } from 'rxjs/operators'
import { AlertService } from 'src/app/components/_alert/alert.service';
import { TesoreriaService } from './tesoreria.service';
import { CesionFacturasService } from './cesion-facturas.service';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { ContribucionesService } from './contribuciones.service';
import { EmpresaEnUnDiaService } from './empresa-en-un-dia.service';
import { TagService } from './tag.service';
import { ReporteFullService } from './reporte-full.service';
import { ReporteStateService } from './reporte-state.service';
import { DireccionTrabajoService } from './direccion-trabajo.service';
import { ModuloCobranzaService } from './modulo-cobranza.service';
import { MercadoPublicoService } from './mercado-publico.service';
import { IdataDescargas } from 'src/app/models/mercado-publico/mercado-publico-listado-descargas.model';
import { EstadoCesionesResponse, EstadoCesionResponse } from 'src/app/models/cesion-facturas/cesion-facturas.models';
import { UtilsService } from './utils.service';
import { CompraVentaService } from './compra-venta.service';
import { MonitoreoServiciosService } from './monitoreo-servicios.service';
import { IServicio } from 'src/app/models/monitoreo-servicios/monitoreo-servicios.model';

@Injectable({
  providedIn: 'root'
})
export class AsincronoService {
  private reporteState = inject(ReporteStateService);

  private readonly STATUS_TX = {
    COMPLETED: 'COMPLETED',
    ERROR: 'ERROR'
  } as const

  private pollingEstadoServicios$ = new Subject<void>();
  private hasServiceErrorsSubject = new BehaviorSubject<boolean>(false);
  public hasServiceErrors$ = this.hasServiceErrorsSubject.asObservable();

  constructor(
    private alertService: AlertService,
    private tesoreriaService: TesoreriaService,
    private empresaEnUnDiaService: EmpresaEnUnDiaService,
    private cesionFacturasService: CesionFacturasService,
    private contribucionesService: ContribucionesService,
    private tagService: TagService,
    private reporteFullService: ReporteFullService,
    private direccionTrabajoService: DireccionTrabajoService,
    private moduloCobranzaService: ModuloCobranzaService,
    private mercadoPublicoService: MercadoPublicoService,
    private _sessionService: SessionService,
    private utilsService: UtilsService,
    private compraVentaService: CompraVentaService,
    private monitoreoServiciosService: MonitoreoServiciosService,
    private router: Router
  ) { }

  llamadaTesoreria(idTransaccion: string, rut: string): void {
    const maxRetries = 44;
    let idTXValue: string | null = null;
    let messageError: string = '';

    timer(0, 30000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.tesoreriaService.buscarTX(idTransaccion)),
      map(response => 
        { 
          const message = response?.TesoreriaReporte?.DatosBasicosSolicitud;
          let isSatisfactory = false;
          if(message && message?.EstadoTransaccion === 'COMPLETED' && message?.SubEstadoTransaccion === 'COMPLETED'){
            idTXValue = idTransaccion;
            isSatisfactory = true
          } else if (message && message?.EstadoTransaccion === 'ERROR'){
            isSatisfactory = true
            if(message?.SubEstadoTransaccion === 'PROCESS'){
              messageError = 'Ha ocurrido un error durante el procesamiento del reporte de tesorería.';
            } else if (message?.SubEstadoTransaccion === 'PASSWORD') {
              messageError = 'Ha ocurrido un error durante el procesamiento del reporte de tesorería, las credenciales no son válidas para acceder al Sii.';
            } else if (message?.SubEstadoTransaccion === 'RUT-VALIDATION') {
              messageError = 'Ha ocurrido un error durante el procesamiento del reporte de tesorería, Rut no es válido.';
            }
          } else {
            isSatisfactory = false;
          }

          return { response: response, isSatisfactory: isSatisfactory }
          
        }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
      finalize(() => {
        //Termina la ejecucion ira a identificar si existe this.idTXValue
      })
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte de tesorería.');
      },
      () => {
        if(!idTXValue){
          if(messageError != '') {
            this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos del reporte de tesorería.');
          }
        } else {
          const variables: any = {
            rut: rut,
            idTransaccion: idTXValue
          }
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte de tesorería ${idTXValue}`, {autoClose: true, buttonRedirect: true, componente: 'reportes/reporte-tesoreria/buscar', arguments: variables});
        }
      }
    );
  }

  llamadaEmpresaEnUnDiaV2(idTransaccion: string, rut: string): void {
    const maxRetries = 20;
    let idTXValue: string | null = null;
    let messageError = '';

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.empresaEnUnDiaService.searchTX(idTransaccion)),
      map(response => {
        const datosSolicitud = response?.EmpresaEnUnDiaReporte?.DatosBasicosSolicitud;
        const certificados = response?.EmpresaEnUnDiaReporte?.Certificados || [];
        let isSatisfactory = false;
        if(datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.COMPLETED && datosSolicitud?.SubEstadoTransaccion === this.STATUS_TX.COMPLETED && certificados.length > 0) {
          idTXValue = idTransaccion;
          isSatisfactory = true
        } else if (datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.ERROR) {
          isSatisfactory = true
          messageError = 'Empresa en un día - Ha ocurrido un error en la generación del reporte.';
        } else {
          isSatisfactory = false;
        }

        return { response: response, isSatisfactory: isSatisfactory }
      }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
      finalize(() => {
        //Termina la ejecucion ira a identificar si existe this.idTXValue
      })
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte de empresa en un dia.');
      },
      () => {
        if(!idTXValue){
          this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos del reporte de empresa en un dia.');
        } else {
          const variables: any = {
            rut: rut,
            idTransaccion: idTXValue
          }
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte de empresa en un dia ${idTXValue}`, {
            autoClose: true,
            buttonRedirect: true,
            componente: 'reportes/empresa-en-un-dia/certificados',
            arguments: variables
        });
        }
      }
    );
  }

  llamadaEmpresaEnUnDiaActuacion(idTransaccion: string, rut: string): void {
    const maxRetries = 20;
    let idTXValue: string | null = null;
    let messageError = '';

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.empresaEnUnDiaService.searchTX(idTransaccion)),
      map(response => {
        const datosSolicitud = response?.EmpresaEnUnDiaReporte?.DatosBasicosSolicitud;
        let isSatisfactory = false;
        if(datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.COMPLETED && datosSolicitud?.SubEstadoTransaccion === this.STATUS_TX.COMPLETED) {
          idTXValue = idTransaccion;
          isSatisfactory = true
        } else if (datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.ERROR) {
          isSatisfactory = true
          messageError = 'Empresa en un día - Ha ocurrido un error en la generación del reporte actuaciones.';
        } else {
          isSatisfactory = false;
        }

        return { response: response, isSatisfactory: isSatisfactory }
      }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
      finalize(() => {
        //Termina la ejecucion ira a identificar si existe this.idTXValue
      })
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte actuaciones de empresa en un dia.');
      },
      () => {
        if(!idTXValue){
          this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos del reporte actuaciones de empresa en un dia.');
        } else {
          const variables: any = {
            rut: rut,
            idTransaccion: idTXValue
          }
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte actuaciones de empresa en un dia ${idTXValue}`, {
            autoClose: true,
            buttonRedirect: true,
            componente: 'reportes/empresa-en-un-dia/actuaciones',
            arguments: variables
        });
        }
      }
    );
  }

  llamadaCreacionCesionFacturas(organizacion: string, rut: string, idTransaccionCedente: string, idTransaccionCesionario: string, fechaInicio: string, fechaFin: string): void {
    
    const maxRetries = 22;
    const retryInterval = 15000; // Intervalo de 15 segundos
    let messageError1: string = '';
    let messageError2: string = '';

    let isSatisfactory1 = false;
    let isSatisfactory2 = false;
  
    timer(0, retryInterval)
      .pipe(
        take(maxRetries),
        mergeMap(() => forkJoin([
          this.cesionFacturasService.obtenerReportePorId(organizacion, idTransaccionCedente),
          this.cesionFacturasService.obtenerReportePorId(organizacion, idTransaccionCesionario)
        ])),
        map(([response1, response2]: any[]) => {
          const datosSolicitud1 = response1?.CesionFacturasReporte?.DatosBasicosSolicitud || null;
          const datosSolicitud2 = response2?.CesionFacturasReporte?.DatosBasicosSolicitud || null;

          if (datosSolicitud1 && datosSolicitud1?.EstadoTransaccion === 'COMPLETED' && datosSolicitud1?.SubEstadoTransaccion === 'COMPLETED') {
            isSatisfactory1 = true;
          } else if (datosSolicitud1 && datosSolicitud1?.EstadoTransaccion === 'ERROR') {
            isSatisfactory1 = true;
            messageError1 = 'Cesión de Facturas - Ha ocurrido un error en la generación del reporte de cesión de facturas.';
          }

          if (datosSolicitud2 && datosSolicitud2?.EstadoTransaccion === 'COMPLETED' && datosSolicitud2?.SubEstadoTransaccion === 'COMPLETED') {
            isSatisfactory2 = true;
          } else if (datosSolicitud2 && datosSolicitud2?.EstadoTransaccion === 'ERROR') {
            isSatisfactory2 = true;
            messageError2 = 'Cesión de Facturas - Ha ocurrido un error en la generación del reporte de cesión de facturas.';
          }

          return { response1, response2, isSatisfactory1, isSatisfactory2 };
        }),
        takeWhile(({ isSatisfactory1, isSatisfactory2 }) => !isSatisfactory1 || !isSatisfactory2)
      )
      .subscribe(
        ({ response1, response2, isSatisfactory1, isSatisfactory2 }) => {
          // NO SE EJECUTA
        },
        error => {
          this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte de cesión de facturas.');
        },
        () => {
          if(isSatisfactory1 && isSatisfactory2){
            if(messageError1 != '' || messageError2 != ''){
              this.alertService.error(messageError1 || messageError2 || 'Ha ocurrido un error al obtener datos del reporte de cesión de facturas.');
            } else {
              this.actualizarDTECesionFacturas(organizacion, rut, fechaInicio, fechaFin);
            }
          } else {
            this.alertService.error(messageError1 || messageError2 || 'Ha ocurrido un error al obtener datos del reporte de cesión de facturas.');
          }
        }
      );
  }

  actualizarDTECesionFacturas(organizacion: string, rut: string, fechaInicio: string, fechaFin: string): void {
    this.cesionFacturasService.actualizarDTE(organizacion, rut, fechaInicio, fechaFin).subscribe(
      (data: EstadoCesionesResponse) => {
        this.lladamaObtenerDTECesionFacturas(organizacion, rut, fechaInicio, fechaFin);
      },
      ({ error }) => {
        this.alertService.error(error.message || 'Ha ocurrido un error al actualizar el reporte de cesión de facturas.');
    });

  }

  lladamaObtenerDTECesionFacturas(organizacion: string, rut: string, fechaInicio: string, fechaFin: string): void {
    const maxRetries = 20;
    let done: boolean = false;
    let messageError: string = '';

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.cesionFacturasService.obtenerEstadoDTE(organizacion, rut, fechaInicio, fechaFin)),
      map((response: EstadoCesionResponse) => 
        {  
          let isSatisfactory = false;
          if(response?.estado && response?.estado === 'completado'){
            done = true;
            isSatisfactory = true
          } else if (response?.estado && response?.estado === 'error'){
            isSatisfactory = true
            messageError = 'Cesión de Facturas - Ha ocurrido un error al actualizar el reporte de cesión de facturas.';
          } else {
            isSatisfactory = false;
          }

          return { response: response, isSatisfactory: isSatisfactory }
          
        }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
      finalize(() => {
        //Termina la ejecucion ira a identificar si existe this.idTXValue
      })
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte de cesión de facturas.');
      },
      () => {
        if(!done){
          this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos del reporte de cesión de facturas.');
        } else {
          if(organizacion == this._sessionService.getOrganization()){
            if (this.router.url === '/reportes/monitoreo-cesion-facturas') { //'/cesion-facturas/monitoreo'
              // recargar la pagina
              this.router.navigateByUrl('', { skipLocationChange: true }).then(() => {
                this.router.navigate(['/reportes/monitoreo-cesion-facturas']); ///cesion-facturas/monitoreo
                this.alertService.success(`Se ha ejecutado correctamente la creación del reporte de cesión de facturas.`);
              });
            } else {
              this.alertService.success(`Se ha ejecutado correctamente la creación del reporte de cesión de facturas, haga click aquí para ir al modulo de monitoreo`, {autoClose: true, buttonRedirect: true, componente: '/reportes/monitoreo-cesion-facturas'}); //cesion-facturas/monitoreo
            }
          } else {
            this.alertService.success(`Se ha ejecutado correctamente la creación del reporte de cesión de facturas.`, {autoClose: true});
          }
        }
      }
    );
  }

  llamadaAvaluoFiscal(idTransaccion: string, rut: string): void {
    const maxRetries = 20;
    let idTXValue: string | null = null;
    let messageError = '';

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.contribucionesService.searchTX(idTransaccion)),
      map(response => 
        { 
          const message = response?.DatosBasicosSolicitud;
          let isSatisfactory = false;
          if (message && message?.EstadoTransaccion === 'COMPLETED' && message?.SubEstadoTransaccion === 'COMPLETED') {
            idTXValue = idTransaccion;
            isSatisfactory = true
          } else if (message && message?.EstadoTransaccion === 'ERROR') {
            isSatisfactory = true
            if(message?.SubEstadoTransaccion === 'PROCESS') {
              messageError = 'Ha ocurrido un error durante el procesamiento del reporte de avalúo fiscal.';
            } else if (message?.SubEstadoTransaccion === 'PASSWORD') {
              messageError = 'Ha ocurrido un error durante el procesamiento del reporte de avalúo fiscal, las credenciales no son válidas para acceder al Sii.';
            } else if (message?.SubEstadoTransaccion === 'RUT-VALIDATION') {
              messageError = 'Ha ocurrido un error durante el procesamiento del reporte de avalúo fiscal, Rut no es válido.';
            } else {
              messageError = 'Ha ocurrido un error durante el procesamiento del reporte de avalúo fiscal.';
            }
          } else {
            isSatisfactory = false;
          }

          return { response: response, isSatisfactory: isSatisfactory }
          
        }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
      finalize(() => {
        //Termina la ejecucion ira a identificar si existe this.idTXValue
      })
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte de avalúo fiscal.');
      },
      () => {
        if(!idTXValue){
          if(messageError != '') {
            this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos del reporte de avalúo fiscal.');
          }
        } else {
          const variables: any = {
            rut: rut,
            idTransaccion: idTXValue
          }
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte de avalúo fiscal ${idTXValue}`, {autoClose: true, buttonRedirect: true, componente: 'reportes/reporte-avaluo-fiscal/buscar', arguments: variables});
        }
      }
    );
  }

  llamadaTag(idTransaccion: string, rut: string): void {
    const maxRetries = 20;
    let idTXValue: string | null = null;
    let messageError = '';

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.tagService.searchTX(idTransaccion)),
      map(response => {
        const datosSolicitud = response?.DatosBasicosSolicitud;
        let isSatisfactory = false;
        if(datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.COMPLETED && datosSolicitud?.SubEstadoTransaccion === this.STATUS_TX.COMPLETED) {
          idTXValue = idTransaccion;
          isSatisfactory = true
        } else if (datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.ERROR) {
          isSatisfactory = true
          messageError = 'Reporte TAG - Ha ocurrido un error en la generación del reporte.';
        } else {
          isSatisfactory = false;
        }

        return { response: response, isSatisfactory: isSatisfactory }
      }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte TAG.');
      },
      () => {
        if(!idTXValue) {
          this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos del reporte TAG.');
        } else {
          const variables: any = {
            rut: rut,
            idTransaccion: idTXValue
          }
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte TAG ${idTXValue}`, {
            autoClose: true,
            buttonRedirect: true,
            componente: 'reportes/tag/buscar',
            arguments: variables
        });
        }
      }
    );
  }

  llamadaCreacionCTE(idTransaccion: string, rut: string, url: string = '', idUnico?: string, idsReportes?: any[]): void {
    const maxRetries = 12;
    let idTXValue: string | null = null;
    let messageError: string = '';
    let calculated: boolean = false;
    const fromConsolidado = url && url.startsWith('/reporte-consolidado/consultar') ? true : false;

    if(fromConsolidado && idUnico) {
      this.reporteState.actualizarSolicitud(idUnico, idsReportes ? idsReportes : [], 'pendiente', rut, url);
    }

    timer(0, 30000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.reporteFullService.obtenerReporte(rut, idTransaccion)),
      map((response: any) => 
        { 
          const message = response?.CarpetaTributariaElectronicaReporte?.DatosBasicosSolicitud;
          let isSatisfactory = false;
          if (message && message?.EstadoTransaccion === 'COMPLETED' && message?.SubEstadoTransaccion === 'COMPLETED-CALCULATED') {
            idTXValue = idTransaccion;
            calculated = true;
            isSatisfactory = true
          } else if (message && message?.EstadoTransaccion === 'ERROR' || message?.SubEstadoTransaccion === 'ERROR'){
            isSatisfactory = true
            messageError = 'Ha ocurrido un error durante la creación del reporte de Carpeta Tributaria.';
          } else if(message && message?.EstadoTransaccion === 'COMPLETED'){
            idTXValue = idTransaccion;
            isSatisfactory = true
          } else {
            isSatisfactory = false;
          }

          return { response: response, isSatisfactory: isSatisfactory }
          
        }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
      finalize(() => {
        //Termina la ejecucion ira a identificar si existe this.idTXValue
      })
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al intentar crear el reporte de Carpeta Tributaria.');
      },
      () => {
        if(!idTXValue){
          if(messageError != '') {
            this.alertService.error(messageError || 'Ha ocurrido un error al crear el reporte de Carpeta Tributaria.');
          } else {
            this.alertService.error('Ha ocurrido un error al crear el reporte de Carpeta Tributaria.');
          }

          if(fromConsolidado && idUnico) {
            this.reporteState.actualizarSolicitud(idUnico, [], 'error');
          }
        } else {
          if(!calculated) {
            this.calcularVentasCTE(idTXValue, rut, url, idUnico);
          } else {
            this.goToCTE(idTransaccion, rut, url, idUnico);
          }
        }
      }
    );
  }

  calcularVentasCTE(idTransaccion: string, rut: string, url: string = '', idUnico?: string): void {
    const fromConsolidado = url && url.startsWith('/reporte-consolidado/consultar') ? true : false;

    this.reporteFullService.calcularVentas(rut, idTransaccion).subscribe(
      (response: any) => {
        this.goToCTE(idTransaccion, rut, url, idUnico);
      },
      (error: any) => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al calcular el reporte de Carpeta Tributaria.');
        if(fromConsolidado && idUnico) {
          this.reporteState.actualizarSolicitud(idUnico, [], 'error');
        }
      }
    );
  }

  goToCTE(idTransaccion: string, rut: string, url: string = '', idUnico?: string): void {
    const fromConsolidado = url && url.startsWith('/reporte-consolidado/consultar') ? true : false;

    const variables: any = {
      rut: rut,
      idTransaccion: idTransaccion,
      reporte: 'cte'
    }

    if(!url){
      url = '/reportes/reporte-financiero';
    }

    if (this.router.url.startsWith(url)) {
      if(fromConsolidado) {
        if(idUnico) {
          this.reporteState.actualizarSolicitud(idUnico, [], 'completado');
        }
      } else {
        this.router.navigateByUrl('', { skipLocationChange: true }).then(() => {
          this.router.navigate([`${url}/automatico`], { queryParams: variables});
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte de Carpeta Tributaria.`);
        });
      }
    } else {
      if(fromConsolidado) {
        if(idUnico) {
          const urls = this.reporteState.obtenerReporte(idUnico);
          if(urls?.idsReportes && urls?.idsReportes.length > 0) {
            const variablesConsolidado: any = {
              rut: rut
            };
            for (let i = 0; i < urls?.idsReportes.length; i++) {
              variablesConsolidado[urls?.idsReportes[i]?.reporte] = urls?.idsReportes[i]?.idTransaccion;
            }

            this.alertService.success(
              `Se ha ejecutado correctamente la creación del reporte de Carpeta Tributaria, haga click aquí para ir al modulo de Reporte Consolidado`, 
              {
                //autoClose: true, 
                buttonRedirect: true, 
                componente: url,
                arguments: variablesConsolidado
              }
            );
          }

          setTimeout(() => {
            this.reporteState.actualizarSolicitud(idUnico, [], 'error');
          }, 1000);
        }
      } else {
        this.alertService.success(
          `Se ha ejecutado correctamente la creación del reporte de Carpeta Tributaria, haga click aquí para ir al modulo de Reporte Financiero`, 
          {
            //autoClose: true, 
            buttonRedirect: true, 
            componente: `${url}/automatico`,
            arguments: variables
          }
        );
      }

    }
  }

  llamadaMultas(idTransaccion: string, rut: string): void {
    const maxRetries = 20;
    let idTXValue: string | null = null;
    let messageError = '';

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.direccionTrabajoService.searchTX(idTransaccion)),
      map(response => {
        const datosSolicitud = response?.DatosBasicosSolicitud;
        let isSatisfactory = false;
        if(datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.COMPLETED && datosSolicitud?.SubEstadoTransaccion === this.STATUS_TX.COMPLETED) {
          idTXValue = idTransaccion;
          isSatisfactory = true
        } else if (datosSolicitud && datosSolicitud?.EstadoTransaccion === this.STATUS_TX.ERROR) {
          isSatisfactory = true
          messageError = 'Reporte multas - Ha ocurrido un error en la generación del reporte.';
        } else {
          isSatisfactory = false;
        }

        return { response: response, isSatisfactory: isSatisfactory }
      }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al obtener datos del reporte de multas.');
      },
      () => {
        if(!idTXValue) {
          this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos del reporte de multas.');
        } else {
          const variables: any = {
            rut: rut,
            idTransaccion: idTXValue
          }
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte multas ${idTXValue}`, {
            autoClose: true,
            buttonRedirect: true,
            componente: 'reportes/reporte-multas/buscar',
            arguments: variables
        });
        }
      }
    );
  }

  llamadaVisionCliente(identificador: string): void {
    const maxRetries = 20;

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.moduloCobranzaService.getVisionCliente(identificador)),
      map(response => ({
        response,
        isSatisfactory: !response?.reporteConsolidado.nuevoEnProgreso
      })),
      filter(({ isSatisfactory }) => isSatisfactory),
      take(1)
    )
    .subscribe({
      next: (res) => {
          const queryParams = {
            tab: 'vision-cliente',
            rut: res.response.identificador,
          }

          this.alertService.success(`Los datos de cobranza del cliente con Rut ${res.response.identificador} se han actualizado correctamente.`, {
            autoClose: true,
            buttonRedirect: true,
            componente: 'modulo-cobranza',
            arguments: queryParams
        });
      },
      error: ({error}) => {
        this.alertService.error(error?.message || 'Ocurrió un error al intentar obtener los datos del cliente.');
      }
    });
  }

  pulingObtenerArchivoMercadoPublico(idTransaccionArchivo: string): void {
    const maxRetries = 40;
    let messageError = '';
    let data: IdataDescargas | null = null;

    timer(0, 15000)
    .pipe(
      take(maxRetries),
      mergeMap(() => this.mercadoPublicoService.obtenerListadoSolicitudDescarga()),
      map(response => {
        let isSatisfactory = false;
        
        if(!response?.data || response?.data.length <= 0) {
          isSatisfactory = true;
          messageError = 'No se ha logrado recuperar la información de los archivos.';
        } else {
          const element = response?.data?.find(e => e.idTransaccion === idTransaccionArchivo);
          if(!element) {
            isSatisfactory = true;
            messageError = 'No se ha logrado recuperar la información del archivo.';
          } else if(element && element?.estado === 'COMPLETED' && element?.subEstado === 'COMPLETED') {
            data = element;
            isSatisfactory = true;
          } else if (element && element?.estado === 'ERROR') {
            isSatisfactory = true;
            messageError = 'Ha ocurrido un error en la generación del reporte.';
          } else {
            isSatisfactory = false;
          }
        }

        return { response: response, isSatisfactory: isSatisfactory }
      }),
      takeWhile(({ response, isSatisfactory }) => !isSatisfactory),
    )
    .subscribe(
      response => {
        // Llega al ultimo pero debe estar esta definicion
      },
      error => {
        this.alertService.error(error?.error?.message || 'Ha ocurrido un error al intentar obtener datos de las descargas.');
      },
      () => {
        if(messageError) {
          this.alertService.error(messageError || 'Ha ocurrido un error al obtener datos de las descargas.');
        } else if(data && Object.keys(data).length > 0) {
          const url = data?.urlDescarga;
          if(url) {
            this.alertService.success(
              "Su archivo de mercado público está listo para descargar. Haga click aquí para descargarlo.", 
              { 
                autoClose: false, // Que no se cierre sola para que le de tiempo a hacer click
                urlExterna: url 
              }
            );
          } else {
            this.alertService.error('Ha ocurrido un error al obtener la url de descarga.');
          }
        }
      }
    );
  }

  iniciarPollingCompraVenta(idUnico: string, rut: string, periodosLCV: number[], idTxCesion: string | null, idsReportes: any[]): void {
    const maxRetries = 21;
    const intervalMs = 15000; // 15 segundos entre llamadas

    // Variables de control locales al flujo
    let lcvTerminado = periodosLCV.length === 0;
    let cesionTerminada = idTxCesion === null;
    let errorFatalLCV = false;
    let idsReportesFinales: any[] = idsReportes;

    timer(0, intervalMs)
      .pipe(
        take(maxRetries),
        mergeMap(() => {
          const apiServices$: any = {};

          if (!lcvTerminado) {
            apiServices$.lcv = this.crearPollingLCV(rut, periodosLCV);
          }
          if (!cesionTerminada && idTxCesion) {
            apiServices$.cesion = this.crearPollingCesion(idTxCesion);
          }

          // Si no hay nada que consultar, devolvemos un observable vacio para salir
          if (Object.keys(apiServices$).length === 0) {
             return of({ terminado: true });
          }

          return forkJoin(apiServices$);
        }),
        map((resultados: any) => {
          if (resultados.terminado) return { done: true };

          // --- Evaluacion de LCV ---
          if (resultados.lcv) {
            if (resultados.lcv.error) {
              lcvTerminado = true;
              const erroresRedLCV = periodosLCV.map(periodo => ({
                reporte: 'lcv',
                idTransaccion: '', // lcv no usa idtx
                status: 'error',
                extraData: { periodo: periodo, msg: 'Ha ocurrido un error al consultar el reporte de libro compra venta' }
              }));
              
              idsReportesFinales = this.utilsService.fusionarIdsReportes(idsReportesFinales, erroresRedLCV);

              // Error de fatal
              const tieneAlgunCompletado = idsReportesFinales.some(e => e.reporte === 'lcv' && e.status === 'completado');
              if (!tieneAlgunCompletado) {
                errorFatalLCV = true;
              }
            } else {
              const resumenLCV = this.utilsService.procesarRespuestaLCVPuente(resultados.lcv.data);
              
              periodosLCV = [...new Set(resumenLCV.pendientes.map(p => p.extraData.periodo))];
              idsReportesFinales = this.utilsService.fusionarIdsReportes(idsReportesFinales, resumenLCV.completados);
              idsReportesFinales = this.utilsService.fusionarIdsReportes(idsReportesFinales, resumenLCV.errores);
  
              if (periodosLCV.length === 0) {
                lcvTerminado = true;
                const todosFallaron = resumenLCV.completados.length === 0 && this.utilsService.idsReportesContienenSoloErroresLCV(idsReportesFinales);
                if (todosFallaron) {
                  errorFatalLCV = true;
                }
              }
            }
          }

          // --- Evaluacion de Cesion ---
          if (resultados.cesion) {
            let objCesion: any = null;
            if (resultados.cesion.error) {
              cesionTerminada = true;
              objCesion = { reporte: 'cesion', status: 'error', extraData: resultados?.cesion?.error || '' };
            } else {
              const estadoCesion = resultados.cesion.data?.CesionFacturasReporte?.DatosBasicosSolicitud;
              if (estadoCesion?.EstadoTransaccion === 'COMPLETED') {
                 cesionTerminada = true;
                 objCesion = { reporte: 'cesion', idTransaccion: idTxCesion, status: 'completado' };
              } else if (estadoCesion?.EstadoTransaccion === 'ERROR' || estadoCesion?.SubEstadoTransaccion === 'ERROR') {
                 cesionTerminada = true;
                 objCesion = { reporte: 'cesion', status: 'error', extraData: 'Ha ocurrido un error al consultar el reporte de cesión de facturas' };
              }
              // Si es IN-PROGRESS, cesionTerminada sigue siendo false
            }

            if(objCesion)
              idsReportesFinales = this.utilsService.fusionarIdsReportes(idsReportesFinales, [objCesion]);
          }
          return { done: (lcvTerminado && cesionTerminada) || errorFatalLCV };
        }),
        takeWhile(({ done }) => !done, true), // true para emitir el ultimo valor
      )
      .subscribe({
        error: (error) => {
          this.alertService.error('Ocurrió un error al intentar crear el reporte libro compra venta.');
          this.reporteState.actualizarSolicitud(idUnico, idsReportesFinales, 'error');
        },
        complete: () => {
          idsReportesFinales = idsReportesFinales.map(reporte => {
            if (reporte.status === 'pendiente') {
              if(reporte.reporte == 'cesion') {
                return { ...reporte, status: 'error', extraData: 'Ocurrio un error inesperado' };
              } else if (reporte.reporte == 'lcv') {
                return { ...reporte, status: 'error', extraData: { ...reporte.extraData, msg: 'Ocurrio un error inesperado' } };
              }
            }
            return reporte;
          });

          const allLCVCompleted = idsReportesFinales.filter(e => e.reporte == 'lcv').every(e => e.status == 'completado');
          const someLCVCompleted = idsReportesFinales.filter(e => e.reporte == 'lcv').some(e => e.status == 'completado');
          
          this.goToLCV(rut, allLCVCompleted, someLCVCompleted, idUnico, idsReportesFinales);
        }
    });
  }

  goToLCV(rut: string, allLCVCompleted: boolean, someLCVCompleted: boolean, idUnico: string, idsReportesFinales: any[]): void {
    const variables: any = {
      rut: rut,
      fromAsync: true
    }

    const urlCrear = '/reportes/compra-venta/consultar' // puede ser el buscar tambien
    const urlBuscar = '/reportes/compra-venta/buscar' // puede ser el buscar tambien

    if (this.router.url.startsWith(urlCrear) || this.router.url.startsWith(urlBuscar)) { // estamos en modulo
      
      if(someLCVCompleted) {
        this.reporteState.actualizarSolicitud(idUnico, idsReportesFinales, 'completado');
        if(allLCVCompleted) {
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte Compra Venta para el rut ${rut}.`, {autoClose: false});
        } else {
          this.alertService.success(`Se ha ejecutado correctamente la creación de algunos reportes Compra Venta para el rut ${rut}.`, {autoClose: false});
        }
      } else {
        this.alertService.error('Ocurrió un error al intentar crear el reporte libro compra venta.');
        this.reporteState.actualizarSolicitud(idUnico, idsReportesFinales, 'error');
      }
    } else { // no estamos en el modulo se debe poder presionar en el mensaje para la redireccion

      if(someLCVCompleted) {
        this.reporteState.actualizarSolicitud(idUnico, idsReportesFinales, 'completado');
        if(allLCVCompleted) {
          this.alertService.success(`Se ha ejecutado correctamente la creación del reporte Compra Venta para el rut ${rut}, haga click aquí para ir al modulo.`, {
            autoClose: false, 
            buttonRedirect: true, 
            componente: urlCrear, // se envia a crear que muestra los resultados
            arguments: variables
          });
        } else {
          this.alertService.success(`Se ha ejecutado correctamente la creación de algunos reportes Compra Venta para el rut ${rut}, haga click aquí para ir al modulo.`, {
            autoClose: false, 
            buttonRedirect: true, 
            componente: urlCrear, // se envia a crear que muestra los resultados
            arguments: variables
          });
        }
      } else {
        this.alertService.error('Ocurrió un error al intentar crear el reporte libro compra venta.');
        this.reporteState.actualizarSolicitud(idUnico, idsReportesFinales, 'error');
      }
    }
  }

  private crearPollingLCV(rut: string, periodosAsync: number[]): Observable<any> {
    return this.compraVentaService.obtenerTx(rut, periodosAsync).pipe(
      map(resp => ({ data: resp, error: null })),
      catchError(error => of({ 
        data: null, 
        error: error?.error?.message || 'Error al consultar Libro Compra Venta' 
      }))
    );
  }

  private crearPollingCesion(idTx: string): Observable<any> {
    return this.cesionFacturasService.obtenerReportePorId('', idTx).pipe(
      map(resp => ({ data: resp, error: null })),
      catchError(error => of({ 
        data: null, 
        error: error?.error?.message || 'Error al consultar reporte de cesión' 
      }))
    );
  }

  iniciarPollingEstadoServicios(): Observable<IServicio[]> {
    const CINCO_MINUTOS = 5 * 60 * 1000;

    this.stopPollingEstadoServicios();

    return timer(0, CINCO_MINUTOS).pipe(
      filter(() => {
        const userInfo = this._sessionService.getUserInfo();
        const idUsuario = userInfo?.email;
        const idOrganizacion = this._sessionService.getOrganization();

        return Boolean(idUsuario && idOrganizacion);
      }),
      switchMap(() => this.monitoreoServiciosService.listServices().pipe(
        catchError(error => {
          console.error('Error al consultar servicios:', error);
          return EMPTY; 
        })
      )),
      tap(servicios => {
        const someError = servicios.some(servicio => servicio.estado === 'Intermitente' || servicio.estado === 'Fuera de Servicio')

        this.hasServiceErrorsSubject.next(someError);
      }),
      takeUntil(this.pollingEstadoServicios$)
    );
  }

  stopPollingEstadoServicios() {
    this.pollingEstadoServicios$.next();
  }

}
